import { Square, Circle, Pentagon, Spline, Minus, MousePointerClick } from 'lucide-react';
import { getAllowedTools } from '../../config';

/**
 * Tool icon mapping (lowercase keys to match DRAWING_TOOLS enum)
 */
const TOOL_ICON_MAP = {
  rectangle: Square,
  circle: Circle,
  polygon: Pentagon,
  bezier: Spline,
  line: Minus,
  point: MousePointerClick,
  centerline: Spline, // #720 Weg-Mittellinie
};

/**
 * Tool label mapping (German)
 */
const TOOL_LABEL_MAP = {
  rectangle: 'Rechteck',
  circle: 'Kreis',
  polygon: 'Polygon',
  bezier: 'Kurve',
  line: 'Linie',
  point: 'Platzieren',
  centerline: 'Mittellinie', // #720 Weg
};

/**
 * Tool name mapping: DRAWING_TOOLS enum values to UI tool names
 * Note: 'bezier' in config maps to 'curve' in UI
 */
const TOOL_NAME_MAP = {
  rectangle: 'rectangle',
  circle: 'circle',
  polygon: 'polygon',
  bezier: 'curve',
  line: 'line',
  point: 'point',
  centerline: 'centerline', // #720 Weg-Mittellinie
};

/**
 * ShapeToolbar - Contextual toolbar showing available drawing tools
 *
 * Features:
 * - Displays horizontally below tab bar in Garten tab
 * - Shows only tools allowed for selected object type
 * - Highlights currently active tool
 * - Shows empty state when no object selected
 *
 * @param {string} selectedObjectType - Currently selected garden object type ID
 * @param {string} activeTool - Currently active drawing tool
 * @param {function} onToolChange - Callback(toolName) when tool is selected
 */
const ShapeToolbar = ({ selectedObjectType, activeTool, onToolChange }) => {
  // Get allowed tools for selected object type
  const allowedTools = selectedObjectType ? getAllowedTools(selectedObjectType) : [];

  // No object selected - show placeholder
  if (!selectedObjectType || allowedTools.length === 0) {
    return (
      <div className="h-[60px] bg-white border-b border-gray-200 flex items-center justify-center">
        <p className="text-sm text-gray-500">
          Wähle ein Gartenobjekt aus der linken Sidebar
        </p>
      </div>
    );
  }

  return (
    <div className="h-[60px] bg-white border-b border-gray-200 flex items-center px-4 gap-2">
      {/* Tool label */}
      <span className="text-sm font-medium text-gray-700 mr-2">
        Werkzeuge:
      </span>

      {/* Tool buttons */}
      {allowedTools.map((toolConfigName) => {
        const IconComponent = TOOL_ICON_MAP[toolConfigName];
        const label = TOOL_LABEL_MAP[toolConfigName];
        const uiToolName = TOOL_NAME_MAP[toolConfigName];
        const isActive = activeTool === uiToolName;

        return (
          <button
            key={toolConfigName}
            onClick={() => onToolChange?.(uiToolName)}
            className={`
              px-4 py-2 rounded flex items-center gap-2
              transition-all duration-150
              ${
                isActive
                  ? 'bg-emerald-500 text-white shadow-sm'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }
            `}
            title={label}
            aria-label={label}
          >
            {IconComponent && (
              <IconComponent
                size={18}
                className={isActive ? 'text-white' : 'text-gray-500'}
              />
            )}
            <span className="text-sm font-medium">{label}</span>
          </button>
        );
      })}
    </div>
  );
};

export default ShapeToolbar;
